import app from "@/main";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import _ from "lodash-es";
import Path from "@/lib/common/path";
import Config from "@/config/config";
import Auth from "@/lib/common/auth";
import vuexStore from "@/store/vuex-store";
import dayjs from "dayjs";
import md5 from "js-md5";
import { Crypto } from "@/lib/model/account/utils";

const handles = new Map<number, Function>();
const axiosInstance = axios.create();
const axiosInstanceWithoutAutoResolve = axios.create();

// 创建一个请求队列
const cancelList = [
  "/aiExam/candidate/list",
  "/aiExam/examVD/list",
  "/aiExam/dashboard/candidateCount"
];
const pending: any[] = [];
const CancelToken = axios.CancelToken;

const removePending = (config: any) => {
  for (let i = 0; i < pending.length; i++) {
    if (pending[i].url === config.url + "&" + config.method) {
      pending[i].cancel("取消上一次请求");
      pending.splice(i, 1);
    }
  }
};

function isFromAdmin() {
  return sessionStorage.getItem("fromAdmin") == "1";
}

const requestMiddleware: any = (config: AxiosRequestConfig) => {
  let data = config.data ? config.data : {};
  data["companyId"] = Auth.companyId;
  data["pageUrlPathString"] = document.URL; //前端页面路径
  //补个examType 后端用
  if (!data.examType) {
    data = { ...data, examType: vuexStore.getExamType };
  }
  if (isFromAdmin()) data["fromAdmin"] = 1;
  config.data = data;
  const headers = config.headers ? config.headers : {};
  headers["AUTHORIZATION"] = "Bearer " + Auth.token;
  // 记录请求，以便取消
  removePending(config);
  config.cancelToken = new CancelToken(c => {
    if (cancelList.some(p => config.url?.includes(p))) {
      pending.push({ cancel: c, url: config.url + "&" + config.method });
    }
  });

  if (config.data?.needAesEncrypt) {
    config.data = Crypto.encryptData(config.data);
  }

  return config;
};

handles.set(302, (resp: AxiosResponse) => {
  location.href = resp.data.url;
  return Promise.reject(resp);
});
//未登录 则清掉缓存 跳登录
handles.set(401, (resp: AxiosResponse) => {
  Auth.logout();
  setTimeout(() => {
    window.location.reload();
  }, 1000);
  return Promise.reject(resp);
});
//4011 牛人客户端未登录 跳登录
handles.set(4011, (resp: AxiosResponse) => {
  localStorage.removeItem("nrToken");
  setTimeout(() => {
    window.location.reload();
  }, 300);
  return Promise.reject(resp);
});
// 需要跳转第三方登录
handles.set(4012, (resp: AxiosResponse) => {
  location.href = resp.data.message;
  return Promise.reject(resp);
});

axiosInstance.interceptors.request.use(requestMiddleware, err => {
  return Promise.reject(err);
});

axiosInstance.interceptors.response.use(
  (resp: AxiosResponse) => {
    // 在请求队列中移除
    removePending(resp.config);

    const data = resp.data;
    if (_.isEmpty(data)) {
      resp.data = {
        code: -500,
        message: "网络错误，请稍后再试"
      };
      return Promise.reject(resp);
    }
    const code = parseInt(data["code"]);
    if (handles.get(code)) {
      const f = handles.get(code);
      if (f) {
        return f(resp);
      }
    }
    //阅卷页特判 406时 不弹错误信息
    try {
      const params = JSON.parse(resp.config.data);
      if (code === 406 && params?.ignore406) {
        return resp;
      }
    } catch (error) {
      console.log(error);
    }

    if (code !== 0) {
      app.$message.error(resp.data.message);
      return Promise.reject(resp);
    }
    return resp;
  },
  err => {
    return Promise.reject(err);
  }
);

axiosInstanceWithoutAutoResolve.interceptors.request.use(
  requestMiddleware,
  err => {
    return Promise.reject(err);
  }
);

axiosInstanceWithoutAutoResolve.interceptors.response.use(
  (resp: AxiosResponse) => {
    const data = resp.data;
    if (_.isEmpty(data)) {
      resp.data = {
        code: -500,
        message: "网络错误，请稍后再试"
      };
      return Promise.reject(resp);
    }
    const code = parseInt(data["code"]);
    if (code != 0) {
      return Promise.reject(resp);
    }
    return resp;
  },
  err => {
    return Promise.reject(err);
  }
);

export class UploadFile {
  name = "";
  status = "";
  size = 0;
  percentage = 0;
  uid = 0;
  raw!: File;
}

class Request {
  /**
   * 生成加密参数
   *  接口要统一加三个参数：
   *  - appCode
   *  - timestamp(秒级别/误差1小时)
   *  - sign = md5({appCode}{timestamp}{appSecret})
   *
   * @param appCode 应用代码
   * @param appSecret 应用密钥
   * @returns 加密后的 URLSearchParams 对象
   */
  protected getEncryptionParams(
    url: string,
    appCode: string,
    appSecret: string
  ): string {
    const urlInfo = new URL(url);
    const params = new URLSearchParams(urlInfo.search);
    const timestamp = dayjs()
      .unix()
      .toString();
    params.append("appCode", appCode);
    params.append("timestamp", timestamp);
    params.append("sign", md5(`${appCode}${timestamp}${appSecret}`));
    urlInfo.search = params.toString();
    return urlInfo.href;
  }

  protected url(pathname: string): string {
    let url =
      pathname.startsWith("http://") || pathname.startsWith("https://")
        ? pathname
        : Path.join(Config.apiUrl, pathname);
    url = this.getEncryptionParams(url, Config.apiAppCode, Config.apiAppSecret);
    return url;
  }

  protected examUrl(pathname: string): string {
    return pathname.startsWith("http://") || pathname.startsWith("https://")
      ? pathname
      : Path.join(Config.examApiUrl, pathname);
  }

  protected componentUrl(pathname: string): string {
    let url =
      pathname.startsWith("http://") || pathname.startsWith("https://")
        ? pathname
        : Path.join(Config.componentUrl, pathname);
    url = this.getEncryptionParams(
      url,
      Config.componentApiAppCode,
      Config.componentApiAppSecret
    );
    return url;
  }

  protected aiInterviewUrl(pathname: string): string {
    const appCode = "ai-hr";
    const appSecret = "PRYkKDyA5R5tGnoW";
    const timestamp = dayjs()
      .unix()
      .toString();
    const sign = md5(`${appCode}${timestamp}${appSecret}`);
    let url =
      pathname.startsWith("http://") || pathname.startsWith("https://")
        ? pathname
        : Path.join(Config.aiInterviewUrl, pathname);
    url = Path.setParam(url, "appCode", appCode);
    url = Path.setParam(url, "timestamp", timestamp);
    url = Path.setParam(url, "sign", sign);
    return url;
  }

  public uploadUrl(pathname: string): string {
    return pathname.startsWith("http://") || pathname.startsWith("https://")
      ? pathname
      : Path.join(Config.apiUrl, pathname);
  }
  post(pathname: string, params?: any, autoResolve = true) {
    const url = this.url(pathname);
    const instance = autoResolve
      ? axiosInstance
      : axiosInstanceWithoutAutoResolve;
    return instance.post(url, params);
  }
  postExamApi(pathname: string, params?: any, autoResolve = true) {
    const url = this.examUrl(pathname);
    const instance = autoResolve
      ? axiosInstance
      : axiosInstanceWithoutAutoResolve;
    return instance.post(url, params);
  }
  postComponentApi(pathname: string, params?: any, autoResolve = true) {
    const url = this.componentUrl(pathname);
    const instance = autoResolve
      ? axiosInstance
      : axiosInstanceWithoutAutoResolve;
    return instance.post(url, params);
  }
  postInterviewApi(pathname: string, params?: any, autoResolve = true) {
    const url = this.aiInterviewUrl(pathname);
    const instance = autoResolve
      ? axiosInstance
      : axiosInstanceWithoutAutoResolve;
    return instance.post(url, params);
  }
  upload(pathname: string, uploadFile: UploadFile) {
    const form = new FormData();
    const con: any = {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent: ProgressEvent) => {
        if (progressEvent.lengthComputable) {
          uploadFile.percentage =
            ((progressEvent.loaded / progressEvent.total) * 100) | 0;
        }
      }
    };
    form.append("file", uploadFile.raw);
    let url = this.url(pathname);
    url = Path.setParam(url, "companyId", Auth.companyId);
    return axiosInstance.post(url, form, con);
  }
}

export default new Request();
